import {catchError, concatMap, defer, EMPTY, finalize, Subject, Subscription} from "rxjs";

type QueuedJob = {
    run: () => Promise<void>,
    done: () => void,
};

/**
 * Runs the tasklist's jobs strictly one after another: everything that changes the rendered block or
 * writes `scrollTop` (extending, trimming, applying a reload, restoring, "back to today", toggling the
 * view). Two such jobs running at once could both measure the same shift and both compensate it.
 *
 * A failing job is logged and does not stop the queue. After `close()` (the view disconnected) waiting
 * jobs are not started; every enqueued promise still resolves. A job must never enqueue another job
 * and await it — it would wait on itself.
 */
export class TasklistJobQueue {

    private readonly _jobs$ = new Subject<QueuedJob>();

    private readonly _subscription: Subscription;

    private _pending = 0;

    private _closed = false;

    public constructor() {
        this._subscription = this._jobs$.pipe(
            concatMap(job => defer(async () => {
                if (!this._closed) {
                    await job.run();
                }
            }).pipe(
                catchError(error => {
                    console.error('tasklist job failed', error);
                    return EMPTY;
                }),
                finalize(() => {
                    this._pending--;
                    job.done();
                }),
            )),
        ).subscribe();
    }

    /** True while a job is waiting or running. */
    public get busy(): boolean {
        return this._pending > 0;
    }

    /** Appends a job; the promise resolves once it has run (or was skipped after close). */
    public enqueue(run: () => Promise<void>): Promise<void> {
        if (this._closed) {
            return Promise.resolve();
        }

        return new Promise<void>(resolve => {
            this._pending++;
            this._jobs$.next({run, done: resolve});
        });
    }

    /** Stops starting new jobs. The running job finishes; waiting jobs resolve without running. */
    public close(): void {
        this._closed = true;
    }

    /** Releases the subscription (after close, once nothing is pending). */
    public dispose(): void {
        this.close();
        this._subscription.unsubscribe();
    }
}
